import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import {
  Alert,
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Spinner,
} from "reactstrap";

import ClientPicker from "../../../../Components/Studio/ClientPicker";
import { isSlotInPast, todayIsoDate } from "../../../../helpers/agendaDateTime";
import {
  applyPreferredOrder,
  differsFromPreferredOrder,
  moveIdInOrder,
  reorderIds,
} from "../../../../helpers/extraServiceOrder";
import {
  getExtraOrderPreference,
  setExtraOrderPreference,
} from "../../../../helpers/extraOrderPreference";
import { resolveCurrentTenantId } from "../../../../helpers/agendaUnitContext";
import {
  findPreferredSlot,
  shouldLoadUnitCatalog,
  wallClockTimeLabel,
} from "./appointmentCreatePrefill";
import { addMinutesToDatetimeLocal, wallClockToDatetimeLocal } from "./absenceRange";
import { hasSlotListGap } from "./slotListGap";
import { formatMoneyFromCents } from "../../../../helpers/money";
import {
  Appointment,
  AvailabilitySlot,
  create,
  CreateAppointmentPayload,
  DepositCollectionMethod,
  getAll as listAppointments,
  getAvailability,
  update,
} from "../../../../services/appointmentService";
import {
  filterDepositMethodsByEffective,
  getMethodsWithLegacyFallback,
  resolveDefaultDepositMethod,
} from "../../../../services/paymentMethodSettingsService";
import {
  getEmailNotificationSettings,
  isEmailUsageNearLimit,
  type EmailNotificationUsage,
} from "../../../../services/emailNotificationSettingsService";
import {
  getMessagingSettings,
  isWhatsAppChannelAvailable,
  isWhatsAppDeliveryPossible,
  isWhatsAppUsageNearLimit,
  type MessagingHealth,
  type MessagingSettings,
  type MessagingUsage,
} from "../../../../services/messagingSettingsService";
import { getClientById } from "../../../../services/clientService";
import {
  getProfessionals,
  Professional,
} from "../../../../services/professionalService";
import {
  confirmClinicalGateContinue,
  isClinicalGateAckRequired,
  extractClinicalGateAlertsFromError,
} from "../../../../helpers/promptClinicalGate";
import { getClientClinicalGate } from "../../../../services/clientService";
import {
  getServiceProfessionals,
  ServiceProfessionalsData,
} from "../../../../services/professionalServiceEnablementService";
import { getServices, Service } from "../../../../services/serviceService";
import {
  bookServiceBundle,
  getBundleAvailability,
  getServiceBundles,
} from "../../../../services/serviceBundleService";
import {
  DEFAULT_TENANT_SETTINGS,
  getSettings,
  TenantSettings,
} from "../../../../services/settingsService";
import {
  ServiceBundle,
  ServiceBundleAvailabilitySlot,
} from "../../../../types/serviceBundle";
import { getUnits, Unit } from "../../../../services/unitService";

const CATALOG_PAGE_SIZE = 100;

export type BookingType = "single" | "bundle";

type AppointmentFormValues = {
  unit_id: string;
  professional_id: string;
  client_id: string;
  service_id: string;
  /** Complementos avulsos (GAP-F2-087) — ids de serviços extras, não combo. */
  extra_service_ids: number[];
  notes: string;
  service_bundle_id: string;
  date: string;
  slot_key: string;
  bundle_slot_key: string;
  deposit_percent_bps: string;
  deposit_collection_method: DepositCollectionMethod | "";
  deposit_collection_note: string;
  notify_on_created: boolean;
  notify_reminder: boolean;
};

export type AppointmentFormMode = "create" | "edit";

export type AppointmentCreatePrefill = {
  unit_id?: string;
  professional_id?: string;
  service_id?: string;
  date?: string;
  /** Wall-clock start clicked on the calendar (`YYYY-MM-DD HH:mm:ss`). */
  preferred_start?: string;
};

export type AppointmentFormSuccessOptions = {
  openAppointmentId?: number;
};

export type AppointmentFormOffcanvasProps = {
  isOpen: boolean;
  mode?: AppointmentFormMode;
  editingAppointment?: Appointment | null;
  onClose: () => void;
  onSuccess: (options?: AppointmentFormSuccessOptions) => Promise<void>;
  initialClientId?: string;
  createPrefill?: AppointmentCreatePrefill | null;
  onRegisterAbsence?: (range: { professionalId: string; startLocal: string; endLocal: string }) => void;
  onPixCreated?: (pix: {
    appointmentId: number;
    qrCodeBase64: string;
    copyPasteString: string;
  }) => void;
};

const emptyFormValues = (
  clientId = "",
  notificationDefaults?: { notify_on_created: boolean; notify_reminder: boolean }
): AppointmentFormValues => ({
  unit_id: "",
  professional_id: "",
  client_id: clientId,
  service_id: "",
  extra_service_ids: [],
  notes: "",
  service_bundle_id: "",
  date: todayIsoDate(),
  slot_key: "",
  bundle_slot_key: "",
  deposit_percent_bps: "",
  deposit_collection_method: "",
  deposit_collection_note: "",
  notify_on_created: notificationDefaults?.notify_on_created ?? true,
  notify_reminder: notificationDefaults?.notify_reminder ?? true,
});

const normalizeAppointmentDateTime = (value: string): string => {
  const normalized = value.slice(0, 19).replace("T", " ");

  return normalized.length === 19 ? normalized : value;
};

const appointmentToFormValues = (appointment: Appointment): AppointmentFormValues => {
  const startDatetime = normalizeAppointmentDateTime(appointment.start_datetime);
  const endDatetime = normalizeAppointmentDateTime(appointment.end_datetime);

  return {
    unit_id: String(appointment.unit_id),
    professional_id: String(appointment.professional_id),
    client_id: String(appointment.client_id),
    service_id: String(appointment.service_id ?? ""),
    extra_service_ids: (appointment.extra_services ?? []).map((item) => item.id),
    notes: appointment.notes ?? "",
    service_bundle_id: "",
    date: startDatetime.slice(0, 10),
    slot_key: `${startDatetime}|${endDatetime}`,
    bundle_slot_key: "",
    deposit_percent_bps: "",
    deposit_collection_method: "",
    deposit_collection_note: "",
    notify_on_created: appointment.notify_on_created ?? true,
    notify_reminder: appointment.notify_reminder ?? true,
  };
};

const slotKey = (slot: AvailabilitySlot | ServiceBundleAvailabilitySlot): string =>
  `${slot.start_datetime}|${slot.end_datetime}`;

const parseSlotKey = (key: string): { start_datetime: string; end_datetime: string } | null => {
  const separatorIndex = key.indexOf("|");

  if (separatorIndex === -1) {
    return null;
  }

  return {
    start_datetime: key.slice(0, separatorIndex),
    end_datetime: key.slice(separatorIndex + 1),
  };
};

const formatTimeLabel = (datetime: string): string => datetime.slice(11, 16);

const resolveDepositOptions = (
  service: Service | undefined,
  tenantSettings: TenantSettings
): number[] => {
  if (service?.deposit_percent_options && service.deposit_percent_options.length > 0) {
    return service.deposit_percent_options;
  }

  return tenantSettings.deposit_percent_options;
};

const buildCreateFormValues = (
  clientId: string,
  prefill?: AppointmentCreatePrefill | null,
  notificationDefaults?: { notify_on_created: boolean; notify_reminder: boolean }
): AppointmentFormValues => ({
  ...emptyFormValues(clientId, notificationDefaults),
  unit_id: prefill?.unit_id ?? "",
  professional_id: prefill?.professional_id ?? "",
  service_id: prefill?.service_id ?? "",
  date: prefill?.date ?? todayIsoDate(),
});

const AppointmentFormOffcanvas = ({
  isOpen,
  mode = "create",
  editingAppointment = null,
  onClose,
  onSuccess,
  initialClientId,
  createPrefill = null,
  onRegisterAbsence,
  onPixCreated,
}: AppointmentFormOffcanvasProps) => {
  const { t, i18n } = useTranslation();
  const isEditMode = mode === "edit" && editingAppointment !== null;

  const [bookingType, setBookingType] = useState<BookingType>("single");
  const [units, setUnits] = useState<Unit[]>([]);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [serviceBundles, setServiceBundles] = useState<ServiceBundle[]>([]);
  const [tenantSettings, setTenantSettings] = useState<TenantSettings>(DEFAULT_TENANT_SETTINGS);
  const [submitting, setSubmitting] = useState(false);
  const [availabilitySlots, setAvailabilitySlots] = useState<AvailabilitySlot[]>([]);
  const [bundleAvailabilitySlots, setBundleAvailabilitySlots] = useState<
    ServiceBundleAvailabilitySlot[]
  >([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [loadingBundleAvailability, setLoadingBundleAvailability] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [loadingBundles, setLoadingBundles] = useState(false);
  const [servicesLoadedUnitId, setServicesLoadedUnitId] = useState("");
  const [bundlesLoadedUnitId, setBundlesLoadedUnitId] = useState("");
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [serviceEnablement, setServiceEnablement] = useState<ServiceProfessionalsData | null>(
    null
  );
  const [loadingServiceEnablement, setLoadingServiceEnablement] = useState(false);
  const [effectiveDepositMethods, setEffectiveDepositMethods] = useState<DepositCollectionMethod[]>(
    []
  );
  const [extrasSearchQuery, setExtrasSearchQuery] = useState("");
  const [lastVisitExtras, setLastVisitExtras] = useState<{
    ids: number[];
    names: string[];
  } | null>(null);
  const [loadingLastVisitExtras, setLoadingLastVisitExtras] = useState(false);
  /** Contagem de uso como extra (par profissional + serviço principal, 30 dias). */
  const [extraFrequencyById, setExtraFrequencyById] = useState<Record<number, number>>({});
  /** Snapshot dos extras antes de “Repetir última visita” — permite desfazer. */
  const [extrasBeforeLastVisitApply, setExtrasBeforeLastVisitApply] = useState<number[] | null>(
    null
  );
  const [extraDragFromIndex, setExtraDragFromIndex] = useState<number | null>(null);
  const [focusedExtraIndex, setFocusedExtraIndex] = useState(0);
  const [extrasOrderUndoSnapshot, setExtrasOrderUndoSnapshot] = useState<
    number[] | null
  >(null);
  const pendingSlotStartRef = useRef<string | null>(null);
  const [emailNotificationDefaults, setEmailNotificationDefaults] = useState({
    notify_on_created: true,
    notify_reminder: true,
  });
  const [emailUsage, setEmailUsage] = useState<EmailNotificationUsage | null>(null);
  const [clientEmailNotificationsEnabled, setClientEmailNotificationsEnabled] = useState(true);
  const [emailFeatureSettings, setEmailFeatureSettings] = useState({
    appointment_created_notifications_enabled: true,
    appointment_reminder_notifications_enabled: true,
  });
  const [messagingSettings, setMessagingSettings] = useState<MessagingSettings | null>(null);
  const [messagingHealth, setMessagingHealth] = useState<MessagingHealth | null>(null);
  const [whatsAppUsage, setWhatsAppUsage] = useState<MessagingUsage | null>(null);
  const [selectedClientHasPhone, setSelectedClientHasPhone] = useState<boolean | null>(null);

  const restrictProfessionalServices = tenantSettings.restrict_professional_services;

  const formInitialValues = useMemo(() => {
    if (isEditMode && editingAppointment) {
      return appointmentToFormValues(editingAppointment);
    }

    return buildCreateFormValues(initialClientId ?? "", createPrefill);
  }, [createPrefill, editingAppointment, initialClientId, isEditMode]);

  const formatMoney = useCallback(
    (cents: number) => formatMoneyFromCents(cents, i18n.language),
    [i18n.language]
  );

  const formatTimeRange = useCallback(
    (start: string, end: string) =>
      t("studio.agenda.fields.slot.option", {
        start: formatTimeLabel(start),
        end: formatTimeLabel(end),
      }),
    [t]
  );

  const singleSchema = useMemo(
    () =>
      Yup.object({
        unit_id: Yup.string().required(t("studio.agenda.validation.unitRequired")),
        professional_id: Yup.string().required(t("studio.agenda.validation.professionalRequired")),
        client_id: Yup.string().required(t("studio.agenda.validation.clientRequired")),
        service_id: Yup.string().required(t("studio.agenda.validation.serviceRequired")),
        date: Yup.string().required(t("studio.agenda.validation.dateRequired")),
        slot_key: Yup.string().required(t("studio.agenda.validation.slotRequired")),
        deposit_percent_bps: Yup.string().when("service_id", {
          is: (serviceId: string) => {
            const service = services.find((item) => item.id === Number(serviceId));
            return service?.require_deposit === true;
          },
          then: (schema) => schema.required(t("studio.agenda.validation.depositPercentRequired")),
          otherwise: (schema) => schema.notRequired(),
        }),
        deposit_collection_method: Yup.string().when("service_id", {
          is: (serviceId: string) => {
            const service = services.find((item) => item.id === Number(serviceId));
            return service?.require_deposit === true;
          },
          then: (schema) =>
            schema
              .required(t("studio.agenda.validation.depositCollectionMethodRequired"))
              .oneOf(
                effectiveDepositMethods.length > 0 ? [...effectiveDepositMethods] : ["__none__"],
                t("studio.agenda.validation.depositCollectionMethodInvalid")
              ),
          otherwise: (schema) => schema.notRequired(),
        }),
        deposit_collection_note: Yup.string().when("deposit_collection_method", {
          is: "other",
          then: (schema) => schema.trim().max(500),
          otherwise: (schema) => schema.notRequired(),
        }),
        notes: Yup.string().trim().max(2000, t("studio.agenda.validation.notesMax")),
      }),
    [effectiveDepositMethods, services, t]
  );

  const bundleSchema = useMemo(
    () =>
      Yup.object({
        unit_id: Yup.string().required(t("studio.agenda.validation.unitRequired")),
        client_id: Yup.string().required(t("studio.agenda.validation.clientRequired")),
        service_bundle_id: Yup.string().required(t("studio.agenda.validation.bundleRequired")),
        date: Yup.string().required(t("studio.agenda.validation.dateRequired")),
        bundle_slot_key: Yup.string().required(t("studio.agenda.validation.slotRequired")),
      }),
    [t]
  );

  const editSchema = useMemo(
    () =>
      Yup.object({
        unit_id: Yup.string().required(t("studio.agenda.validation.unitRequired")),
        professional_id: Yup.string().required(t("studio.agenda.validation.professionalRequired")),
        client_id: Yup.string().required(t("studio.agenda.validation.clientRequired")),
        service_id: Yup.string().required(t("studio.agenda.validation.serviceRequired")),
        date: Yup.string().required(t("studio.agenda.validation.dateRequired")),
        slot_key: Yup.string().required(t("studio.agenda.validation.slotRequired")),
        notes: Yup.string().trim().max(2000, t("studio.agenda.validation.notesMax")),
      }),
    [t]
  );

  const appointmentForm = useFormik<AppointmentFormValues>({
    initialValues: formInitialValues,
    enableReinitialize: true,
    validationSchema: isEditMode
      ? editSchema
      : bookingType === "single"
        ? singleSchema
        : bundleSchema,
    onSubmit: async (values, helpers) => {
      const schema = isEditMode
        ? editSchema
        : bookingType === "single"
          ? singleSchema
          : bundleSchema;

      try {
        await schema.validate(values, { abortEarly: false });
      } catch (validationError) {
        if (validationError instanceof Yup.ValidationError) {
          const errors = validationError.inner.reduce<Record<string, string>>((acc, issue) => {
            if (issue.path && !acc[issue.path]) {
              acc[issue.path] = issue.message;
            }

            return acc;
          }, {});

          helpers.setErrors(errors);
          return;
        }
      }

      setSubmitting(true);

      try {
        if (isEditMode && editingAppointment) {
          const slot = parseSlotKey(values.slot_key);

          if (!slot) {
            helpers.setFieldError("slot_key", t("studio.agenda.validation.slotRequired"));
            return;
          }

          if (
            Number(values.professional_id) !== editingAppointment.professional_id
          ) {
            const confirmation = await Swal.fire({
              title: t("studio.agenda.actions.edit.professionalChangeTitle"),
              text: t("studio.agenda.actions.edit.professionalChangeConfirm"),
              icon: "warning",
              showCancelButton: true,
              confirmButtonText: t("studio.agenda.actions.edit.professionalChangeConfirmButton"),
              cancelButtonText: t("studio.agenda.actions.edit.professionalChangeDismiss"),
              focusCancel: true,
            });

            if (!confirmation.isConfirmed) {
              return;
            }
          }

          const isBundleEdit = Boolean(
            editingAppointment.service_bundle_id || editingAppointment.bundle_group_id
          );

          const response = await update(editingAppointment.id, {
            professional_id: Number(values.professional_id),
            service_id: Number(values.service_id),
            ...(isBundleEdit
              ? {}
              : { extra_service_ids: values.extra_service_ids }),
            notes: values.notes.trim() === "" ? null : values.notes.trim(),
            start_datetime: slot.start_datetime,
            notify_on_created: values.notify_on_created,
            notify_reminder: values.notify_reminder,
          });

          if (!isBundleEdit && values.extra_service_ids.length > 0) {
            setExtraOrderPreference(
              resolveCurrentTenantId(),
              Number(values.professional_id),
              Number(values.service_id),
              values.extra_service_ids
            );
            toast.info(t("studio.agenda.fields.extraServices.orderSavedToast"));
          }
          toast.success(response.message || t("studio.agenda.edit_success"));
          handleClose();
          await onSuccess();
          return;
        }

        if (bookingType === "bundle") {
          const slot = bundleAvailabilitySlots.find(
            (item) => slotKey(item) === values.bundle_slot_key
          );

          if (!slot) {
            helpers.setFieldError("bundle_slot_key", t("studio.agenda.validation.slotRequired"));
            return;
          }

          const response = await bookServiceBundle(Number(values.service_bundle_id), {
            unit_id: Number(values.unit_id),
            client_id: Number(values.client_id),
            start_datetime: slot.start_datetime,
            items: slot.items.map((item) => ({
              sort_order: item.sort_order,
              professional_id: item.professional_id,
            })),
            notify_on_created: values.notify_on_created,
            notify_reminder: values.notify_reminder,
          });

          toast.success(response.message || t("studio.agenda.toast.createSuccess"));
          handleClose();
          const booked = response.data?.appointments ?? [];
          const lead = [...booked].sort((left, right) =>
            left.start_datetime.localeCompare(right.start_datetime)
          )[0];
          await onSuccess(
            lead?.id ? { openAppointmentId: lead.id } : undefined
          );
          return;
        }

        const slot = parseSlotKey(values.slot_key);

        if (!slot) {
          helpers.setFieldError("slot_key", t("studio.agenda.validation.slotRequired"));
          return;
        }

        if (values.extra_service_ids.length > 0) {
          const selectedIds = new Set([
            Number(values.service_id),
            ...values.extra_service_ids.map(Number),
          ]);
          const matchingBundle =
            serviceBundles.find((bundle) => {
              if (!bundle.is_active || !bundle.items?.length) {
                return false;
              }
              const itemIds = new Set(bundle.items.map((item) => item.service_id));
              if (itemIds.size !== selectedIds.size) {
                return false;
              }
              return Array.from(selectedIds).every((id) => itemIds.has(id));
            }) ?? null;

          if (matchingBundle) {
            const confirmation = await Swal.fire({
              title: t("studio.agenda.fields.extraServices.matchingBundleConfirmTitle"),
              text: t("studio.agenda.fields.extraServices.matchingBundleConfirmText", {
                name: matchingBundle.name,
              }),
              icon: "question",
              showDenyButton: true,
              showCancelButton: true,
              confirmButtonText: t(
                "studio.agenda.fields.extraServices.matchingBundleConfirmContinue"
              ),
              denyButtonText: t(
                "studio.agenda.fields.extraServices.matchingBundleConfirmUseBundle"
              ),
              cancelButtonText: t(
                "studio.agenda.fields.extraServices.matchingBundleConfirmCancel"
              ),
              focusCancel: true,
            });

            if (confirmation.isDenied) {
              void handleBookingTypeChange("bundle").then(() => {
                helpers.setFieldValue("service_bundle_id", String(matchingBundle.id));
              });
              return;
            }

            if (!confirmation.isConfirmed) {
              return;
            }
          }
        }

        const createPayload: CreateAppointmentPayload = {
          unit_id: Number(values.unit_id),
          professional_id: Number(values.professional_id),
          client_id: Number(values.client_id),
          service_id: Number(values.service_id),
          start_datetime: slot.start_datetime,
          end_datetime: slot.end_datetime,
        };

        if (values.extra_service_ids.length > 0) {
          createPayload.extra_service_ids = values.extra_service_ids;
        }

        const trimmedAppointmentNotes = values.notes.trim();

        if (trimmedAppointmentNotes !== "") {
          createPayload.notes = trimmedAppointmentNotes;
        }

        if (values.deposit_percent_bps) {
          createPayload.deposit_percent_bps = Number(values.deposit_percent_bps);
        }

        if (values.deposit_collection_method) {
          createPayload.deposit_collection_method = values.deposit_collection_method;
        }

        const trimmedNote = values.deposit_collection_note.trim();

        if (values.deposit_collection_method === "other" && trimmedNote !== "") {
          createPayload.deposit_collection_note = trimmedNote;
        }

        createPayload.notify_on_created = values.notify_on_created;
        createPayload.notify_reminder = values.notify_reminder;

        // Check clinical gate before creating (GAP-F2-120)
        const allServiceIds = [
          Number(values.service_id),
          ...values.extra_service_ids,
        ].filter(Boolean);

        if (values.client_id && allServiceIds.length > 0) {
          try {
            const gatePreview = await getClientClinicalGate(
              Number(values.client_id),
              { service_ids: allServiceIds }
            );

            if (
              gatePreview.data?.clinical_gate_alerts &&
              gatePreview.data.clinical_gate_alerts.length > 0
            ) {
              const mayProceed = await confirmClinicalGateContinue({
                t,
                alerts: gatePreview.data.clinical_gate_alerts,
              });

              if (!mayProceed) {
                setSubmitting(false);
                return;
              }

              createPayload.acknowledge_clinical_gate = true;
            }
          } catch {
            // Preview is best-effort; soft_block still enforced on create.
          }
        }

        let response;
        try {
          response = await create(createPayload);
        } catch (createError: unknown) {
          if (!isClinicalGateAckRequired(createError)) {
            throw createError;
          }

          const mayProceed = await confirmClinicalGateContinue({
            t,
            alerts: extractClinicalGateAlertsFromError(createError),
          });

          if (!mayProceed) {
            return;
          }

          createPayload.acknowledge_clinical_gate = true;
          response = await create(createPayload);
        }

        if (values.extra_service_ids.length > 0) {
          setExtraOrderPreference(
            resolveCurrentTenantId(),
            Number(values.professional_id),
            Number(values.service_id),
            values.extra_service_ids
          );
          toast.info(t("studio.agenda.fields.extraServices.orderSavedToast"));
        }
        toast.success(response.message || t("studio.agenda.toast.createSuccess"));
        handleClose();

        const shouldOpenPix =
          values.deposit_collection_method === "gateway_pix" &&
          Boolean(response.data?.pix?.qr_code_base64) &&
          Boolean(response.data?.pix?.copy_paste_string);

        if (shouldOpenPix) {
          await onSuccess();
          onPixCreated?.({
            appointmentId: response.data!.id,
            qrCodeBase64: response.data!.pix!.qr_code_base64,
            copyPasteString: response.data!.pix!.copy_paste_string,
          });
        } else {
          await onSuccess(
            response.data?.id ? { openAppointmentId: response.data.id } : undefined
          );
        }
      } catch (error: any) {
        if (error?.code === "SLOT_UNAVAILABLE") {
          toast.error(error.data);
          helpers.setFieldError("slot_key", error.data);
          return;
        }

        toast.error(
          error?.data ||
            (isEditMode
              ? t("studio.agenda.toast.editError")
              : t("studio.agenda.toast.createError"))
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    values: {
      unit_id: formUnitId,
      professional_id: formProfessionalId,
      service_id: formServiceId,
      extra_service_ids: formExtraServiceIds,
      notes: formNotes,
      client_id: formClientId,
      service_bundle_id: formBundleId,
      date: formDate,
      deposit_percent_bps: formDepositPercentBps,
      deposit_collection_method: formDepositCollectionMethod,
      deposit_collection_note: formDepositCollectionNote,
      slot_key: formSlotKey,
      bundle_slot_key: formBundleSlotKey,
      notify_on_created: formNotifyOnCreated,
      notify_reminder: formNotifyReminder,
    },
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    handleBlur,
    handleChange,
    resetForm,
    setErrors,
    setTouched,
    touched,
    errors,
  } = appointmentForm;

  const handleClose = () => {
    setBookingType("single");
    setExtrasSearchQuery("");
    setLastVisitExtras(null);
    pendingSlotStartRef.current = null;
    resetForm({
      values: isEditMode && editingAppointment
        ? appointmentToFormValues(editingAppointment)
        : emptyFormValues(initialClientId ?? "", emailNotificationDefaults),
    });
    setAvailabilitySlots([]);
    setBundleAvailabilitySlots([]);
    setServices([]);
    setServiceBundles([]);
    setLoadingServices(false);
    setLoadingBundles(false);
    setServicesLoadedUnitId("");
    setBundlesLoadedUnitId("");
    setServiceEnablement(null);
    setEffectiveDepositMethods([]);
    setEmailUsage(null);
    setOptionsLoaded(false);
    onClose();
  };

  const loadServiceEnablement = useCallback(async (serviceId: string) => {
    if (!serviceId) {
      setServiceEnablement(null);
      return;
    }

    setLoadingServiceEnablement(true);

    try {
      const response = await getServiceProfessionals(Number(serviceId));
      setServiceEnablement(response.data);
    } catch {
      setServiceEnablement(null);
    } finally {
      setLoadingServiceEnablement(false);
    }
  }, []);

  const loadFormOptions = useCallback(async () => {
    try {
      const [unitsResponse, professionalsResponse, settingsResponse, emailSettingsResponse, messagingSettingsResponse] =
        await Promise.all([
          getUnits({ per_page: CATALOG_PAGE_SIZE }),
          getProfessionals({ per_page: CATALOG_PAGE_SIZE }),
          getSettings(),
          getEmailNotificationSettings(),
          getMessagingSettings(),
        ]);

      setUnits(unitsResponse.data);
      setProfessionals(professionalsResponse.data);
      setTenantSettings(settingsResponse.data);

      const emailSettings = emailSettingsResponse.data.settings;
      const defaults = {
        notify_on_created: emailSettings.notify_on_created_default,
        notify_reminder: emailSettings.notify_reminder_default,
      };

      setEmailNotificationDefaults(defaults);
      setEmailUsage(emailSettingsResponse.data.usage);
      setClientEmailNotificationsEnabled(emailSettings.client_email_notifications_enabled);
      setEmailFeatureSettings({
        appointment_created_notifications_enabled:
          emailSettings.appointment_created_notifications_enabled,
        appointment_reminder_notifications_enabled:
          emailSettings.appointment_reminder_notifications_enabled,
      });

      setMessagingSettings(messagingSettingsResponse.data.settings);
      setMessagingHealth(messagingSettingsResponse.data.health);
      setWhatsAppUsage(messagingSettingsResponse.data.usage);

      if (!isEditMode) {
        setFieldValue("notify_on_created", defaults.notify_on_created);
        setFieldValue("notify_reminder", defaults.notify_reminder);
      }

      const paymentMethodsResponse = await getMethodsWithLegacyFallback();
      setEffectiveDepositMethods(
        filterDepositMethodsByEffective(paymentMethodsResponse.data.effective.deposit)
      );
    } catch (error: any) {
      toast.error(error?.data || t("studio.agenda.toast.loadError"));
      setTenantSettings(DEFAULT_TENANT_SETTINGS);
      setEffectiveDepositMethods([]);
    } finally {
      setOptionsLoaded(true);
    }
  }, [isEditMode, setFieldValue, t]);

  useEffect(() => {
    if (!formClientId) {
      setSelectedClientHasPhone(null);
      return;
    }

    let cancelled = false;
    const clientId = Number(formClientId);

    if (Number.isNaN(clientId)) {
      setSelectedClientHasPhone(null);
      return;
    }

    getClientById(clientId)
      .then((response) => {
        if (!cancelled) {
          setSelectedClientHasPhone(Boolean(response.data.phone_number?.trim()));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSelectedClientHasPhone(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [formClientId]);

  const whatsAppDeliveryPossible =
    messagingSettings !== null
    && whatsAppUsage !== null
    && messagingHealth !== null
    && isWhatsAppDeliveryPossible(messagingSettings, whatsAppUsage, messagingHealth);

  const showNoClientPhoneWarning =
    whatsAppDeliveryPossible
    && selectedClientHasPhone === false
    && (formNotifyOnCreated || formNotifyReminder);

  const loadServicesForUnit = useCallback(
    async (unitId: number) => {
      setLoadingServices(true);

      try {
        const response = await getServices({
          per_page: CATALOG_PAGE_SIZE,
          unit_id: unitId,
        });
        setServices(response.data);
      } catch (error: any) {
        setServices([]);
        toast.error(error?.data || t("studio.agenda.toast.loadError"));
      } finally {
        setServicesLoadedUnitId(String(unitId));
        setLoadingServices(false);
      }
    },
    [t]
  );

  const loadBundlesForUnit = useCallback(async (unitId: number) => {
    setLoadingBundles(true);

    try {
      const response = await getServiceBundles({
        per_page: CATALOG_PAGE_SIZE,
        unit_id: unitId,
        is_active: true,
      });
      setServiceBundles(response.data);
    } catch (error: any) {
      setServiceBundles([]);
      toast.error(error?.data || t("studio.agenda.toast.loadError"));
    } finally {
      setBundlesLoadedUnitId(String(unitId));
      setLoadingBundles(false);
    }
  }, [t]);

  useEffect(() => {
    if (isOpen && isEditMode) {
      setBookingType("single");
    }
  }, [isEditMode, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    void loadFormOptions();
  }, [isOpen, loadFormOptions]);

  useEffect(() => {
    if (!isOpen || !isEditMode || !editingAppointment) {
      return;
    }

    void loadServicesForUnit(editingAppointment.unit_id);
  }, [editingAppointment, isEditMode, isOpen, loadServicesForUnit]);

  // Carrega combos em background no modo avulso para sugerir match com extras (GAP-F2-087 polish).
  useEffect(() => {
    if (!isOpen || bookingType !== "single" || !formUnitId) {
      return;
    }

    if (formExtraServiceIds.length === 0) {
      return;
    }

    if (bundlesLoadedUnitId === formUnitId && serviceBundles.length > 0) {
      return;
    }

    void loadBundlesForUnit(Number(formUnitId));
  }, [
    bookingType,
    bundlesLoadedUnitId,
    formExtraServiceIds.length,
    formUnitId,
    isOpen,
    loadBundlesForUnit,
    serviceBundles.length,
  ]);

  useEffect(() => {
    if (!shouldLoadUnitCatalog(isOpen, isEditMode, formUnitId)) {
      return;
    }

    const unitId = Number(formUnitId);

    if (!Number.isFinite(unitId) || unitId <= 0) {
      return;
    }

    void loadServicesForUnit(unitId);

    if (bookingType === "bundle") {
      void loadBundlesForUnit(unitId);
    }
  }, [
    bookingType,
    formUnitId,
    isEditMode,
    isOpen,
    loadBundlesForUnit,
    loadServicesForUnit,
  ]);

  useEffect(() => {
    if (!isOpen || isEditMode || !createPrefill?.service_id || !formUnitId) {
      return;
    }

    if (servicesLoadedUnitId !== formUnitId || loadingServices) {
      return;
    }

    const prefillServiceId = createPrefill.service_id;
    const exists = services.some((service) => String(service.id) === prefillServiceId);

    if (exists && formServiceId !== prefillServiceId) {
      setFieldValue("service_id", prefillServiceId);
    }
  }, [
    createPrefill?.service_id,
    formServiceId,
    formUnitId,
    isEditMode,
    isOpen,
    loadingServices,
    services,
    servicesLoadedUnitId,
    setFieldValue,
  ]);

  useEffect(() => {
    if (!isOpen || isEditMode) {
      return;
    }

    setAvailabilitySlots([]);
    setBundleAvailabilitySlots([]);
    setFieldValue("slot_key", "");
    setFieldValue("bundle_slot_key", "");
  }, [isEditMode, isOpen, initialClientId, setFieldValue]);

  useEffect(() => {
    if (!isOpen || !initialClientId) {
      return;
    }

    if (String(formClientId) !== String(initialClientId)) {
      setFieldValue("client_id", initialClientId);
    }
  }, [formClientId, initialClientId, isOpen, setFieldValue]);

  useEffect(() => {
    if (!isOpen || bookingType !== "single" || !restrictProfessionalServices) {
      if (!restrictProfessionalServices) {
        setServiceEnablement(null);
        setLoadingServiceEnablement(false);
      }

      return;
    }

    void loadServiceEnablement(formServiceId);
  }, [
    bookingType,
    formServiceId,
    isOpen,
    loadServiceEnablement,
    restrictProfessionalServices,
  ]);

  const visibleProfessionals = useMemo(() => {
    if (!restrictProfessionalServices || !serviceEnablement?.restrict_mode) {
      return professionals;
    }

    const enabledIds = new Set(
      serviceEnablement.professionals
        .filter((professional) => professional.is_enabled)
        .map((professional) => professional.id)
    );

    return professionals.filter((professional) => enabledIds.has(professional.id));
  }, [professionals, restrictProfessionalServices, serviceEnablement]);

  useEffect(() => {
    if (!restrictProfessionalServices || !serviceEnablement?.restrict_mode) {
      return;
    }

    const selectedId = Number(formProfessionalId);

    if (
      selectedId &&
      !visibleProfessionals.some((professional) => professional.id === selectedId)
    ) {
      setFieldValue("professional_id", "");
      setFieldValue("slot_key", "");
    }
  }, [formProfessionalId, restrictProfessionalServices, serviceEnablement, setFieldValue, visibleProfessionals]);

  const canFetchSingleAvailability = Boolean(
    optionsLoaded && formProfessionalId && formServiceId && formDate
  );

  const canFetchBundleAvailability = Boolean(formBundleId && formUnitId && formDate);

  const formExtraServiceIdsKey = formExtraServiceIds.join(",");

  useEffect(() => {
    if (!isOpen || (!isEditMode && bookingType !== "single")) {
      return;
    }

    if (!canFetchSingleAvailability) {
      setAvailabilitySlots([]);
      setLoadingAvailability(false);
      return;
    }

    const extraServiceIds = formExtraServiceIdsKey
      ? formExtraServiceIdsKey.split(",").map((id) => Number(id))
      : [];

    let cancelled = false;
    setLoadingAvailability(true);

    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const response = await getAvailability(Number(formProfessionalId), {
            date: formDate,
            service_id: Number(formServiceId),
            extra_service_ids: extraServiceIds,
          });
          if (!cancelled) {
            setAvailabilitySlots(response.data);
          }
        } catch (error: any) {
          if (!cancelled) {
            setAvailabilitySlots([]);
            toast.error(error?.data || t("studio.agenda.toast.availabilityError"));
          }
        } finally {
          if (!cancelled) {
            setLoadingAvailability(false);
          }
        }
      })();
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    bookingType,
    canFetchSingleAvailability,
    formDate,
    formExtraServiceIdsKey,
    formProfessionalId,
    formServiceId,
    isEditMode,
    isOpen,
    t,
  ]);

  // Após mudar extras: tenta manter o mesmo horário de início; se não couber, avisa.
  useEffect(() => {
    if (!isOpen || loadingAvailability || pendingSlotStartRef.current === null) {
      return;
    }

    const preferredStart = pendingSlotStartRef.current;
    const match = availabilitySlots.find(
      (slot) => slot.start_datetime === preferredStart
    );

    if (match) {
      setFieldValue("slot_key", slotKey(match));
      pendingSlotStartRef.current = null;
      return;
    }

    pendingSlotStartRef.current = null;
    toast.info(t("studio.agenda.fields.extraServices.slotNoLongerFits"));
  }, [availabilitySlots, isOpen, loadingAvailability, setFieldValue, t]);

  // Frequência de extras (30d) + sugestão da última visita (mesmo cliente + profissional + serviço).
  useEffect(() => {
    if (!isOpen || bookingType !== "single" || !formProfessionalId || !formServiceId) {
      setLastVisitExtras(null);
      setExtraFrequencyById({});
      setLoadingLastVisitExtras(false);
      return;
    }

    let cancelled = false;
    setLoadingLastVisitExtras(Boolean(formClientId));

    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const end = new Date();
          const start = new Date();
          start.setUTCDate(start.getUTCDate() - 30);
          const toYmd = (value: Date) => value.toISOString().slice(0, 10);

          const response = await listAppointments({
            professional_id: Number(formProfessionalId),
            status: "completed",
            start_date: toYmd(start),
            end_date: toYmd(end),
            per_page: 100,
          });

          if (cancelled) {
            return;
          }

          const primaryId = Number(formServiceId);
          const excludeId = editingAppointment?.id;
          const rows = (response.data ?? []).filter(
            (item) =>
              item.service_id === primaryId && (excludeId == null || item.id !== excludeId)
          );

          const frequency: Record<number, number> = {};
          for (const row of rows) {
            for (const extra of row.extra_services ?? []) {
              frequency[extra.id] = (frequency[extra.id] ?? 0) + 1;
            }
          }
          setExtraFrequencyById(frequency);

          if (!formClientId) {
            setLastVisitExtras(null);
            return;
          }

          const clientId = Number(formClientId);
          const last = [...rows]
            .filter(
              (item) =>
                item.client_id === clientId && (item.extra_services?.length ?? 0) > 0
            )
            .sort((a, b) => b.start_datetime.localeCompare(a.start_datetime))[0];

          if (!last?.extra_services?.length) {
            setLastVisitExtras(null);
            return;
          }

          const availableIds = new Set(
            services
              .filter((service) => service.id !== primaryId)
              .map((service) => service.id)
          );
          const orderedLastExtras = [...(last.extra_services ?? [])].sort(
            (left, right) => (left.sort_order ?? 0) - (right.sort_order ?? 0)
          );
          const ids = orderedLastExtras
            .map((item) => item.id)
            .filter((id) => availableIds.has(id));

          if (ids.length === 0) {
            setLastVisitExtras(null);
            return;
          }

          setLastVisitExtras({
            ids,
            names: orderedLastExtras
              .filter((item) => ids.includes(item.id))
              .map((item) => item.name)
              .filter((name): name is string => Boolean(name)),
          });
        } catch {
          if (!cancelled) {
            setLastVisitExtras(null);
            setExtraFrequencyById({});
          }
        } finally {
          if (!cancelled) {
            setLoadingLastVisitExtras(false);
          }
        }
      })();
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    bookingType,
    editingAppointment?.id,
    formClientId,
    formProfessionalId,
    formServiceId,
    isOpen,
    services,
  ]);

  useEffect(() => {
    setExtrasBeforeLastVisitApply(null);
  }, [formClientId, formProfessionalId, formServiceId, isOpen]);

  const lastVisitExtrasAlreadyApplied = useMemo(() => {
    if (!lastVisitExtras) {
      return false;
    }

    return (
      lastVisitExtras.ids.length === formExtraServiceIds.length &&
      lastVisitExtras.ids.every((id) => formExtraServiceIds.includes(id))
    );
  }, [formExtraServiceIds, lastVisitExtras]);

  const canUndoLastVisitExtras =
    extrasBeforeLastVisitApply !== null && lastVisitExtrasAlreadyApplied;

  const preferredSingleSlot = useMemo(
    () =>
      findPreferredSlot(
        availabilitySlots.filter((slot) => !isSlotInPast(slot.start_datetime)),
        createPrefill?.preferred_start
      ),
    [availabilitySlots, createPrefill?.preferred_start]
  );

  useEffect(() => {
    if (
      !isOpen ||
      isEditMode ||
      !preferredSingleSlot ||
      !canFetchSingleAvailability ||
      loadingAvailability
    ) {
      return;
    }

    const nextKey = slotKey(preferredSingleSlot);

    if (formSlotKey !== nextKey) {
      setFieldValue("slot_key", nextKey);
    }
  }, [
    canFetchSingleAvailability,
    formSlotKey,
    isEditMode,
    isOpen,
    loadingAvailability,
    preferredSingleSlot,
    setFieldValue,
  ]);

  useEffect(() => {
    if (!isOpen || bookingType !== "bundle") {
      return;
    }

    if (!canFetchBundleAvailability) {
      setBundleAvailabilitySlots([]);
      setLoadingBundleAvailability(false);
      return;
    }

    let cancelled = false;
    setLoadingBundleAvailability(true);

    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const response = await getBundleAvailability(Number(formBundleId), {
            date: formDate,
            unit_id: Number(formUnitId),
          });
          if (!cancelled) {
            setBundleAvailabilitySlots(response.data);
          }
        } catch (error: any) {
          if (!cancelled) {
            setBundleAvailabilitySlots([]);
            toast.error(error?.data || t("studio.agenda.toast.availabilityError"));
          }
        } finally {
          if (!cancelled) {
            setLoadingBundleAvailability(false);
          }
        }
      })();
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    bookingType,
    canFetchBundleAvailability,
    formBundleId,
    formDate,
    formUnitId,
    isOpen,
    t,
  ]);

  const preferredBundleSlot = useMemo(
    () =>
      findPreferredSlot(
        bundleAvailabilitySlots.filter((slot) => !isSlotInPast(slot.start_datetime)),
        createPrefill?.preferred_start
      ),
    [bundleAvailabilitySlots, createPrefill?.preferred_start]
  );

  useEffect(() => {
    if (
      !isOpen ||
      isEditMode ||
      bookingType !== "bundle" ||
      !preferredBundleSlot ||
      !canFetchBundleAvailability ||
      loadingBundleAvailability
    ) {
      return;
    }

    const nextKey = slotKey(preferredBundleSlot);

    if (formBundleSlotKey !== nextKey) {
      setFieldValue("bundle_slot_key", nextKey);
    }
  }, [
    bookingType,
    canFetchBundleAvailability,
    formBundleSlotKey,
    isEditMode,
    isOpen,
    loadingBundleAvailability,
    preferredBundleSlot,
    setFieldValue,
  ]);

  const selectedService = useMemo(
    () => services.find((service) => service.id === Number(formServiceId)),
    [formServiceId, services]
  );

  const extraServiceCandidates = useMemo(
    () => services.filter((service) => service.id !== Number(formServiceId)),
    [formServiceId, services]
  );

  const filteredExtraCandidates = useMemo(() => {
    const query = extrasSearchQuery.trim().toLocaleLowerCase();
    const filtered =
      query === ""
        ? [...extraServiceCandidates]
        : extraServiceCandidates.filter((service) =>
            service.name.toLocaleLowerCase().includes(query)
          );

    return filtered.sort((a, b) => {
      const freqDiff = (extraFrequencyById[b.id] ?? 0) - (extraFrequencyById[a.id] ?? 0);
      if (freqDiff !== 0) {
        return freqDiff;
      }
      return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    });
  }, [extraFrequencyById, extraServiceCandidates, extrasSearchQuery]);

  const appointmentTotals = useMemo(() => {
    if (!selectedService) {
      return null;
    }

    const extras = services.filter((service) =>
      formExtraServiceIds.includes(service.id)
    );
    const durationMinutes =
      (selectedService.duration_minutes ?? 0) +
      extras.reduce((sum, service) => sum + (service.duration_minutes ?? 0), 0);
    const priceCents =
      (selectedService.price ?? 0) +
      extras.reduce((sum, service) => sum + (service.price ?? 0), 0);

    return {
      durationMinutes,
      priceCents,
      extrasCount: extras.length,
    };
  }, [formExtraServiceIds, selectedService, services]);

  const matchingBundleForExtras = useMemo(() => {
    if (!formServiceId || formExtraServiceIds.length === 0) {
      return null;
    }

    const selectedIds = new Set([Number(formServiceId), ...formExtraServiceIds]);

    return (
      serviceBundles.find((bundle) => {
        if (!bundle.is_active || !bundle.items?.length) {
          return false;
        }

        const itemIds = new Set(bundle.items.map((item) => item.service_id));

        if (itemIds.size !== selectedIds.size) {
          return false;
        }

        return Array.from(selectedIds).every((id) => itemIds.has(id));
      }) ?? null
    );
  }, [formExtraServiceIds, formServiceId, serviceBundles]);

  const selectedBundle = useMemo(
    () =>
      serviceBundles.find(
        (bundle) => bundle.id === Number(formBundleId)
      ),
    [formBundleId, serviceBundles]
  );

  const depositOptions = useMemo(
    () => resolveDepositOptions(selectedService, tenantSettings),
    [selectedService, tenantSettings]
  );

  const visibleAvailabilitySlots = useMemo(
    () => availabilitySlots.filter((slot) => !isSlotInPast(slot.start_datetime)),
    [availabilitySlots]
  );

  const slotsForSelect = useMemo(() => {
    if (!isEditMode || !formSlotKey) {
      return visibleAvailabilitySlots;
    }

    const currentSlot = parseSlotKey(formSlotKey);

    if (!currentSlot) {
      return visibleAvailabilitySlots;
    }

    const currentKey = slotKey(currentSlot);

    if (visibleAvailabilitySlots.some((slot) => slotKey(slot) === currentKey)) {
      return visibleAvailabilitySlots;
    }

    return [currentSlot, ...visibleAvailabilitySlots];
  }, [formSlotKey, isEditMode, visibleAvailabilitySlots]);

  const singleSlotListHasGap = useMemo(
    () => hasSlotListGap(slotsForSelect.map((slot) => slot.start_datetime)),
    [slotsForSelect]
  );

  const visibleBundleAvailabilitySlots = useMemo(
    () => bundleAvailabilitySlots.filter((slot) => !isSlotInPast(slot.start_datetime)),
    [bundleAvailabilitySlots]
  );

  const bundleSlotListHasGap = useMemo(
    () => hasSlotListGap(visibleBundleAvailabilitySlots.map((slot) => slot.start_datetime)),
    [visibleBundleAvailabilitySlots]
  );

  const availabilityEmptyReason = useMemo((): "allPast" | "noSlots" | null => {
    if (loadingAvailability || !canFetchSingleAvailability) {
      return null;
    }

    if (availabilitySlots.length > 0 && visibleAvailabilitySlots.length === 0) {
      return "allPast";
    }

    if (availabilitySlots.length === 0) {
      return "noSlots";
    }

    return null;
  }, [
    availabilitySlots.length,
    canFetchSingleAvailability,
    loadingAvailability,
    visibleAvailabilitySlots.length,
  ]);

  const bundleAvailabilityEmptyReason = useMemo((): "allPast" | "noSlots" | null => {
    if (loadingBundleAvailability || !canFetchBundleAvailability) {
      return null;
    }

    if (bundleAvailabilitySlots.length > 0 && visibleBundleAvailabilitySlots.length === 0) {
      return "allPast";
    }

    if (bundleAvailabilitySlots.length === 0) {
      return "noSlots";
    }

    return null;
  }, [
    bundleAvailabilitySlots.length,
    canFetchBundleAvailability,
    loadingBundleAvailability,
    visibleBundleAvailabilitySlots.length,
  ]);

  const clickedTimeLabel = createPrefill?.preferred_start
    ? wallClockTimeLabel(createPrefill.preferred_start)
    : "";

  const preferredSingleMissing =
    clickedTimeLabel !== "" &&
    !isEditMode &&
    bookingType === "single" &&
    canFetchSingleAvailability &&
    !loadingAvailability &&
    availabilityEmptyReason !== "allPast" &&
    !preferredSingleSlot;

  const preferredBundleMissing =
    clickedTimeLabel !== "" &&
    !isEditMode &&
    bookingType === "bundle" &&
    canFetchBundleAvailability &&
    !loadingBundleAvailability &&
    bundleAvailabilityEmptyReason !== "allPast" &&
    !preferredBundleSlot;

  const servicesCatalogEmpty =
    Boolean(formUnitId) &&
    servicesLoadedUnitId === formUnitId &&
    !loadingServices &&
    services.length === 0;

  const bundlesCatalogEmpty =
    Boolean(formUnitId) &&
    bundlesLoadedUnitId === formUnitId &&
    !loadingBundles &&
    serviceBundles.length === 0;

  useEffect(() => {
    if (!selectedService?.require_deposit || depositOptions.length !== 1) {
      return;
    }

    const singleOption = String(depositOptions[0]);

    if (formDepositPercentBps !== singleOption) {
      setFieldValue("deposit_percent_bps", singleOption);
    }
  }, [depositOptions, formDepositPercentBps, selectedService?.require_deposit, setFieldValue]);

  useEffect(() => {
    if (!selectedService?.require_deposit || effectiveDepositMethods.length === 0) {
      return;
    }

    const nextMethod = resolveDefaultDepositMethod(
      formDepositCollectionMethod,
      effectiveDepositMethods
    );

    if (nextMethod !== formDepositCollectionMethod) {
      setFieldValue("deposit_collection_method", nextMethod);
    }
  }, [
    effectiveDepositMethods,
    formDepositCollectionMethod,
    selectedService?.require_deposit,
    setFieldValue,
  ]);

  const depositMethodsUnavailable =
    bookingType === "single"
    && !isEditMode
    && selectedService?.require_deposit === true
    && optionsLoaded
    && effectiveDepositMethods.length === 0;

  const depositPreviewAmount = useMemo(() => {
    if (!selectedService?.require_deposit || !formDepositPercentBps) {
      return null;
    }

    const bps = Number(formDepositPercentBps);

    if (Number.isNaN(bps)) {
      return null;
    }

    return Math.floor((selectedService.price * bps) / 10000);
  }, [formDepositPercentBps, selectedService]);

  const resolveProfessionalName = useCallback(
    (professionalId: number): string =>
      professionals.find((item) => item.id === professionalId)?.name ??
      t("studio.agenda.common.notAvailable"),
    [professionals, t]
  );

  const resolveBundleServiceName = useCallback(
    (serviceId: number): string => {
      const bundleItem = selectedBundle?.items?.find((item) => item.service_id === serviceId);

      return bundleItem?.service?.name ?? t("studio.agenda.common.notAvailable");
    },
    [selectedBundle?.items, t]
  );

  const handleUnitChange = async (unitId: string) => {
    setFieldValue("unit_id", unitId);
    setFieldValue("professional_id", "");
    setFieldValue("service_id", "");
    setFieldValue("extra_service_ids", []);
    setFieldValue("service_bundle_id", "");
    setFieldValue("slot_key", "");
    setFieldValue("bundle_slot_key", "");
    setFieldValue("deposit_percent_bps", "");
    setFieldValue("deposit_collection_method", "");
    setFieldValue("deposit_collection_note", "");
    setAvailabilitySlots([]);
    setBundleAvailabilitySlots([]);

    if (unitId) {
      const loaders = [loadServicesForUnit(Number(unitId))];

      if (bookingType === "bundle") {
        loaders.push(loadBundlesForUnit(Number(unitId)));
      } else {
        setServiceBundles([]);
      }

      await Promise.all(loaders);
    } else {
      setServices([]);
      setServiceBundles([]);
    }
  };

  const handleBookingTypeChange = async (nextType: BookingType) => {
    setBookingType(nextType);
    setFieldValue("slot_key", "");
    setFieldValue("bundle_slot_key", "");
    setFieldValue("deposit_percent_bps", "");
    setFieldValue("deposit_collection_method", "");
    setFieldValue("deposit_collection_note", "");
    setErrors({});
    setTouched({});
    setAvailabilitySlots([]);
    setBundleAvailabilitySlots([]);

    const unitId = formUnitId;

    if (nextType === "bundle" && unitId) {
      await loadBundlesForUnit(Number(unitId));
    } else {
      setServiceBundles([]);
    }
  };

  const handleServiceChange = (serviceId: string) => {
    setFieldValue("service_id", serviceId);
    setFieldValue("slot_key", "");
    setFieldValue("deposit_percent_bps", "");
    setFieldValue("deposit_collection_method", "");
    setFieldValue("deposit_collection_note", "");
    setFieldValue(
      "extra_service_ids",
      formExtraServiceIds.filter((id) => id !== Number(serviceId))
    );
    setExtrasSearchQuery("");

    if (restrictProfessionalServices) {
      void loadServiceEnablement(serviceId);
    } else {
      setServiceEnablement(null);
    }
  };

  const toggleExtraService = (serviceId: number) => {
    const preferred = getExtraOrderPreference(
      resolveCurrentTenantId(),
      Number(formProfessionalId),
      Number(formServiceId)
    );
    const next = formExtraServiceIds.includes(serviceId)
      ? formExtraServiceIds.filter((id) => id !== serviceId)
      : applyPreferredOrder(
          [...formExtraServiceIds, serviceId].slice(0, 9),
          preferred
        );

    const currentSlot = parseSlotKey(formSlotKey);
    pendingSlotStartRef.current = currentSlot?.start_datetime ?? null;

    setExtrasBeforeLastVisitApply(null);
    setExtrasOrderUndoSnapshot(null);
    setFieldValue("extra_service_ids", next);
    setFieldValue("slot_key", "");
  };

  const moveExtraService = (index: number, direction: -1 | 1) => {
    const next = moveIdInOrder(formExtraServiceIds, index, direction);
    if (next === formExtraServiceIds) {
      return;
    }
    setExtrasBeforeLastVisitApply(null);
    setExtrasOrderUndoSnapshot([...formExtraServiceIds]);
    setFieldValue("extra_service_ids", next);
    setFocusedExtraIndex(Math.max(0, Math.min(next.length - 1, index + direction)));
  };

  const reorderExtraService = (fromIndex: number, toIndex: number) => {
    const next = reorderIds(formExtraServiceIds, fromIndex, toIndex);
    if (next === formExtraServiceIds) {
      return;
    }
    setExtrasBeforeLastVisitApply(null);
    setExtrasOrderUndoSnapshot([...formExtraServiceIds]);
    setFieldValue("extra_service_ids", next);
    setFocusedExtraIndex(toIndex);
  };

  const selectedExtrasInOrder = useMemo(() => {
    return formExtraServiceIds
      .map((id) => services.find((service) => service.id === id))
      .filter((service): service is (typeof services)[number] => Boolean(service));
  }, [formExtraServiceIds, services]);

  const preferredExtraOrder = useMemo(
    () =>
      getExtraOrderPreference(
        resolveCurrentTenantId(),
        Number(formProfessionalId),
        Number(formServiceId)
      ),
    [formProfessionalId, formServiceId]
  );

  const canRestoreHabitualExtraOrder = differsFromPreferredOrder(
    formExtraServiceIds,
    preferredExtraOrder
  );

  const restoreHabitualExtraOrder = async () => {
    if (!preferredExtraOrder?.length) {
      return;
    }
    const next = applyPreferredOrder(formExtraServiceIds, preferredExtraOrder);
    if (next === formExtraServiceIds || next.every((id, index) => id === formExtraServiceIds[index])) {
      return;
    }

    const confirmation = await Swal.fire({
      title: t("studio.agenda.fields.extraServices.restoreHabitualConfirmTitle"),
      text: t("studio.agenda.fields.extraServices.restoreHabitualConfirmMessage"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t(
        "studio.agenda.fields.extraServices.restoreHabitualConfirmApply"
      ),
      cancelButtonText: t(
        "studio.agenda.fields.extraServices.restoreHabitualConfirmCancel"
      ),
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    const currentSlot = parseSlotKey(formSlotKey);
    pendingSlotStartRef.current = currentSlot?.start_datetime ?? null;
    setExtrasBeforeLastVisitApply(null);
    setExtrasOrderUndoSnapshot([...formExtraServiceIds]);
    setFieldValue("extra_service_ids", next);
    setFieldValue("slot_key", "");
  };

  const undoExtrasReorder = () => {
    if (extrasOrderUndoSnapshot === null) {
      return;
    }
    const currentSlot = parseSlotKey(formSlotKey);
    pendingSlotStartRef.current = currentSlot?.start_datetime ?? null;
    setFieldValue("extra_service_ids", extrasOrderUndoSnapshot);
    setExtrasOrderUndoSnapshot(null);
    setFieldValue("slot_key", "");
    toast.info(t("studio.agenda.fields.extraServices.undoReorderToast"));
  };

  const handleExtrasOrderKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (selectedExtrasInOrder.length < 2) {
      return;
    }
    const index = Math.max(
      0,
      Math.min(selectedExtrasInOrder.length - 1, focusedExtraIndex)
    );
    const last = selectedExtrasInOrder.length - 1;

    if (event.key === "Home") {
      event.preventDefault();
      if (index > 0) {
        reorderExtraService(index, 0);
      }
      return;
    }

    if (event.key === "End") {
      event.preventDefault();
      if (index < last) {
        reorderExtraService(index, last);
      }
      return;
    }

    if (event.key === "PageUp") {
      event.preventDefault();
      moveExtraService(index, -1);
      return;
    }

    if (event.key === "PageDown") {
      event.preventDefault();
      moveExtraService(index, 1);
      return;
    }

    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") {
      return;
    }
    event.preventDefault();
    const direction: -1 | 1 = event.key === "ArrowUp" ? -1 : 1;
    moveExtraService(index, direction);
  };

  const applyLastVisitExtras = () => {
    if (!lastVisitExtras || lastVisitExtras.ids.length === 0) {
      return;
    }

    const currentSlot = parseSlotKey(formSlotKey);
    pendingSlotStartRef.current = currentSlot?.start_datetime ?? null;
    const nextIds = lastVisitExtras.ids.slice(0, 9);
    setExtrasBeforeLastVisitApply([...formExtraServiceIds]);
    setExtrasOrderUndoSnapshot(null);
    setFieldValue("extra_service_ids", nextIds);
    setFieldValue("slot_key", "");
  };

  const undoLastVisitExtras = () => {
    if (extrasBeforeLastVisitApply === null) {
      return;
    }

    const currentSlot = parseSlotKey(formSlotKey);
    pendingSlotStartRef.current = currentSlot?.start_datetime ?? null;
    setFieldValue("extra_service_ids", extrasBeforeLastVisitApply);
    setExtrasBeforeLastVisitApply(null);
    setFieldValue("slot_key", "");
  };

  const handleBundleChange = (bundleId: string) => {
    setFieldValue("service_bundle_id", bundleId);
    setFieldValue("bundle_slot_key", "");
  };

  const handleAvailabilityFieldChange = (field: keyof AppointmentFormValues, value: string) => {
    setFieldValue(field, value);
    setFieldValue("slot_key", "");
    setFieldValue("bundle_slot_key", "");
  };

  return (
    <Offcanvas isOpen={isOpen} toggle={handleClose} direction="end" className="border-0">
      <OffcanvasHeader toggle={handleClose}>
        {isEditMode ? t("studio.agenda.form.editTitle") : t("studio.agenda.form.newTitle")}
      </OffcanvasHeader>
      <OffcanvasBody>
        {!isEditMode ? (
          <Nav pills className="nav-pills-custom nav-primary gap-2 mb-4">
            <NavItem>
              <NavLink
                href="#"
                className={bookingType === "single" ? "active" : ""}
                onClick={(event) => {
                  event.preventDefault();
                  handleBookingTypeChange("single");
                }}
              >
                {t("studio.agenda.modal.type.single")}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                href="#"
                className={bookingType === "bundle" ? "active" : ""}
                onClick={(event) => {
                  event.preventDefault();
                  handleBookingTypeChange("bundle");
                }}
              >
                {t("studio.agenda.modal.type.bundle")}
              </NavLink>
            </NavItem>
          </Nav>
        ) : null}

        <Form onSubmit={handleSubmit}>
          <div className="mb-3">
            <Label htmlFor="appointment-unit">{t("studio.agenda.fields.unit.label")}</Label>
            <Input
              id="appointment-unit"
              name="unit_id"
              type="select"
              value={formUnitId}
              onChange={(event) => handleUnitChange(event.target.value)}
              onBlur={handleBlur}
              invalid={!!(touched.unit_id && errors.unit_id)}
              disabled={isEditMode}
            >
              <option value="">{t("studio.agenda.fields.unit.placeholder")}</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </Input>
            <FormFeedback>{errors.unit_id}</FormFeedback>
          </div>

          {(isEditMode || bookingType === "single") ? (
            <>
              <div className="mb-3">
                <Label htmlFor="appointment-service">
                  {t("studio.agenda.fields.service.primaryLabel")}
                </Label>
                {loadingServices ? (
                  <div className="py-2">
                    <Spinner size="sm" color="primary" className="me-2" />
                    {t("studio.agenda.availability.loadingServices")}
                  </div>
                ) : (
                  <Input
                    id="appointment-service"
                    name="service_id"
                    type="select"
                    value={formServiceId}
                    onChange={(event) => handleServiceChange(event.target.value)}
                    onBlur={handleBlur}
                    invalid={!!(touched.service_id && errors.service_id)}
                    disabled={!formUnitId}
                  >
                    <option value="">{t("studio.agenda.fields.service.placeholder")}</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.name} — {formatMoney(service.price)}
                      </option>
                    ))}
                  </Input>
                )}
                <FormFeedback>{errors.service_id}</FormFeedback>
                {servicesCatalogEmpty ? (
                  <Alert color="warning" className="mt-2 mb-0 py-2">
                    <small>{t("studio.agenda.fields.service.empty")}</small>
                  </Alert>
                ) : null}
              </div>

              {formServiceId &&
              !editingAppointment?.service_bundle_id &&
              !editingAppointment?.bundle_group_id &&
              extraServiceCandidates.length > 0 ? (
                <div className="mb-3">
                  <Label className="d-block">
                    {t("studio.agenda.fields.extraServices.label")}
                  </Label>
                  <p className="text-muted small mb-2">
                    {t("studio.agenda.fields.extraServices.hint")}
                  </p>
                  {loadingLastVisitExtras ? (
                    <small className="text-muted d-block mb-2">
                      <Spinner size="sm" className="me-1" />
                      {t("studio.agenda.fields.extraServices.lastVisitLoading")}
                    </small>
                  ) : lastVisitExtras && !lastVisitExtrasAlreadyApplied ? (
                    <Alert color="light" className="py-2 px-3 mb-2 border">
                      <small className="d-block mb-1">
                        {t("studio.agenda.fields.extraServices.lastVisitHint", {
                          names: lastVisitExtras.names.join(", "),
                        })}
                      </small>
                      <Button
                        color="primary"
                        outline
                        size="sm"
                        type="button"
                        onClick={applyLastVisitExtras}
                      >
                        {t("studio.agenda.fields.extraServices.lastVisitCta")}
                      </Button>
                    </Alert>
                  ) : canUndoLastVisitExtras ? (
                    <Alert color="light" className="py-2 px-3 mb-2 border">
                      <small className="d-block mb-1">
                        {t("studio.agenda.fields.extraServices.lastVisitApplied")}
                      </small>
                      <Button
                        color="secondary"
                        outline
                        size="sm"
                        type="button"
                        onClick={undoLastVisitExtras}
                      >
                        {t("studio.agenda.fields.extraServices.lastVisitUndo")}
                      </Button>
                    </Alert>
                  ) : null}
                  {extraServiceCandidates.length > 5 ? (
                    <Input
                      type="search"
                      className="mb-2"
                      value={extrasSearchQuery}
                      onChange={(event) => setExtrasSearchQuery(event.target.value)}
                      placeholder={t("studio.agenda.fields.extraServices.searchPlaceholder")}
                      aria-label={t("studio.agenda.fields.extraServices.searchPlaceholder")}
                    />
                  ) : null}
                  <div className="border rounded p-2" style={{ maxHeight: "10rem", overflowY: "auto" }}>
                    {filteredExtraCandidates.length === 0 ? (
                      <small className="text-muted">
                        {t("studio.agenda.fields.extraServices.searchEmpty")}
                      </small>
                    ) : (
                      filteredExtraCandidates.map((service) => {
                        const checked = formExtraServiceIds.includes(service.id);

                        return (
                          <div className="form-check mb-1" key={service.id}>
                            <Input
                              className="form-check-input"
                              type="checkbox"
                              id={`appointment-extra-${service.id}`}
                              checked={checked}
                              disabled={!checked && formExtraServiceIds.length >= 9}
                              onChange={() => toggleExtraService(service.id)}
                            />
                            <Label
                              className="form-check-label"
                              htmlFor={`appointment-extra-${service.id}`}
                            >
                              {service.name} — {formatMoney(service.price)}
                              <span className="text-muted ms-1">
                                ({service.duration_minutes}{" "}
                                {t("studio.agenda.fields.extraServices.minutesAbbrev")})
                              </span>
                            </Label>
                          </div>
                        );
                      })
                    )}
                  </div>
                  {selectedExtrasInOrder.length > 0 ? (
                    <div className="border rounded p-2 mt-2 bg-light">
                      <small className="fw-medium d-block mb-1">
                        {t("studio.agenda.fields.extraServices.orderLabel")}
                      </small>
                      <small className="text-muted d-block mb-2">
                        {t("studio.agenda.fields.extraServices.orderHint")}
                      </small>
                      <small className="text-muted d-block mb-2">
                        {t("studio.agenda.fields.extraServices.orderKeyboardHint")}
                      </small>
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        {canRestoreHabitualExtraOrder ? (
                          <Button
                            color="success"
                            outline
                            size="sm"
                            type="button"
                            onClick={() => void restoreHabitualExtraOrder()}
                          >
                            {t("studio.agenda.fields.extraServices.restoreHabitualOrder")}
                          </Button>
                        ) : null}
                        {extrasOrderUndoSnapshot !== null ? (
                          <Button
                            color="secondary"
                            outline
                            size="sm"
                            type="button"
                            onClick={undoExtrasReorder}
                          >
                            {t("studio.agenda.fields.extraServices.undoReorder")}
                          </Button>
                        ) : null}
                      </div>
                      <div
                        tabIndex={0}
                        role="listbox"
                        aria-label={t("studio.agenda.fields.extraServices.orderLabel")}
                        onKeyDown={handleExtrasOrderKeyDown}
                        className="rounded"
                        style={{ outline: "none" }}
                      >
                      <ul className="list-unstyled mb-0">
                        {selectedExtrasInOrder.map((service, index) => (
                          <li
                            key={service.id}
                            role="option"
                            aria-selected={focusedExtraIndex === index}
                            className="d-flex align-items-center justify-content-between gap-2 mb-1 rounded px-1 py-1"
                            style={{
                              cursor: "grab",
                              opacity: extraDragFromIndex === index ? 0.55 : 1,
                              transform:
                                extraDragFromIndex === index
                                  ? "scale(1.02)"
                                  : undefined,
                              transition:
                                "opacity 0.15s ease, transform 0.15s ease, background-color 0.15s ease",
                              backgroundColor:
                                extraDragFromIndex === index
                                  ? "rgba(var(--vz-primary-rgb), 0.08)"
                                  : focusedExtraIndex === index
                                    ? "rgba(var(--vz-primary-rgb), 0.04)"
                                    : undefined,
                              boxShadow:
                                focusedExtraIndex === index
                                  ? "inset 0 0 0 1px rgba(var(--vz-primary-rgb), 0.35)"
                                  : undefined,
                            }}
                            draggable
                            onClick={() => setFocusedExtraIndex(index)}
                            onDragStart={() => setExtraDragFromIndex(index)}
                            onDragOver={(event) => {
                              event.preventDefault();
                            }}
                            onDrop={(event) => {
                              event.preventDefault();
                              if (extraDragFromIndex === null) {
                                return;
                              }
                              reorderExtraService(extraDragFromIndex, index);
                              setExtraDragFromIndex(null);
                            }}
                            onDragEnd={() => setExtraDragFromIndex(null)}
                          >
                            <span className="small d-flex align-items-center gap-2">
                              <i
                                className="ri-draggable text-muted"
                                aria-hidden="true"
                                title={t("studio.agenda.fields.extraServices.dragHandle")}
                              />
                              <span className="text-muted">{index + 1}.</span>
                              {service.name}
                            </span>
                            <span className="btn-group btn-group-sm">
                              <Button
                                type="button"
                                color="light"
                                size="sm"
                                disabled={index === 0}
                                aria-label={t(
                                  "studio.agenda.fields.extraServices.moveToStart"
                                )}
                                onClick={() => reorderExtraService(index, 0)}
                              >
                                <i className="ri-skip-up-line" aria-hidden="true" />
                              </Button>
                              <Button
                                type="button"
                                color="light"
                                size="sm"
                                disabled={index === 0}
                                aria-label={t(
                                  "studio.agenda.fields.extraServices.moveUp"
                                )}
                                onClick={() => moveExtraService(index, -1)}
                              >
                                <i className="ri-arrow-up-s-line" aria-hidden="true" />
                              </Button>
                              <Button
                                type="button"
                                color="light"
                                size="sm"
                                disabled={index === selectedExtrasInOrder.length - 1}
                                aria-label={t(
                                  "studio.agenda.fields.extraServices.moveDown"
                                )}
                                onClick={() => moveExtraService(index, 1)}
                              >
                                <i className="ri-arrow-down-s-line" aria-hidden="true" />
                              </Button>
                              <Button
                                type="button"
                                color="light"
                                size="sm"
                                disabled={index === selectedExtrasInOrder.length - 1}
                                aria-label={t(
                                  "studio.agenda.fields.extraServices.moveToEnd"
                                )}
                                onClick={() =>
                                  reorderExtraService(
                                    index,
                                    selectedExtrasInOrder.length - 1
                                  )
                                }
                              >
                                <i className="ri-skip-down-line" aria-hidden="true" />
                              </Button>
                            </span>
                          </li>
                        ))}
                      </ul>
                      </div>
                    </div>
                  ) : null}
                  {appointmentTotals ? (
                    <Alert color="light" className="mt-2 mb-0 py-2 border">
                      <small className="mb-0 fw-medium">
                        {t("studio.agenda.fields.extraServices.totals", {
                          minutes: appointmentTotals.durationMinutes,
                          price: formatMoney(appointmentTotals.priceCents),
                        })}
                      </small>
                      {appointmentTotals.extrasCount > 0 ? (
                        <small className="text-muted d-block">
                          {t("studio.agenda.fields.extraServices.totalsHint", {
                            count: appointmentTotals.extrasCount,
                          })}
                        </small>
                      ) : null}
                    </Alert>
                  ) : null}
                  {formExtraServiceIds.length === 0 ? (
                    <small className="text-muted d-block mt-1">
                      {t("studio.agenda.fields.extraServices.empty")}
                    </small>
                  ) : matchingBundleForExtras ? (
                    <Alert color="info" className="mt-2 mb-0 py-2">
                      <small className="mb-0">
                        {t("studio.agenda.fields.extraServices.matchingBundle", {
                          name: matchingBundleForExtras.name,
                        })}{" "}
                        <Button
                          color="link"
                          className="p-0 align-baseline"
                          onClick={() => {
                            const bundleId = matchingBundleForExtras.id;
                            void handleBookingTypeChange("bundle").then(() => {
                              setFieldValue("service_bundle_id", String(bundleId));
                            });
                          }}
                        >
                          {t("studio.agenda.fields.extraServices.useBundleCta")}
                        </Button>
                      </small>
                    </Alert>
                  ) : (
                    <small className="text-muted d-block mt-2">
                      {t("studio.agenda.fields.extraServices.suggestCombo")}{" "}
                      <Link to="/services">{t("studio.agenda.fields.extraServices.suggestComboLink")}</Link>
                    </small>
                  )}
                </div>
              ) : null}

              <div className="mb-3">
                <Label htmlFor="appointment-notes">
                  {t("studio.agenda.fields.notes.label")}
                </Label>
                <Input
                  id="appointment-notes"
                  name="notes"
                  type="textarea"
                  rows={2}
                  value={formNotes}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  invalid={!!(touched.notes && errors.notes)}
                  placeholder={t("studio.agenda.fields.notes.placeholder")}
                  maxLength={2000}
                />
                <FormFeedback>{errors.notes}</FormFeedback>
                <small className="text-muted">
                  {t("studio.agenda.fields.notes.hint")}
                </small>
              </div>

              <div className="mb-3">
                <Label htmlFor="appointment-professional">
                  {t("studio.agenda.fields.professional.label")}
                </Label>
                {restrictProfessionalServices && loadingServiceEnablement ? (
                  <div className="py-2">
                    <Spinner size="sm" color="primary" className="me-2" />
                    {t("studio.agenda.enablement.loading")}
                  </div>
                ) : (
                  <Input
                    id="appointment-professional"
                    name="professional_id"
                    type="select"
                    value={formProfessionalId}
                    onChange={(event) =>
                      handleAvailabilityFieldChange("professional_id", event.target.value)
                    }
                    onBlur={handleBlur}
                    invalid={!!(touched.professional_id && errors.professional_id)}
                    disabled={!formServiceId}
                  >
                    <option value="">{t("studio.agenda.fields.professional.placeholder")}</option>
                    {visibleProfessionals.map((professional) => (
                      <option key={professional.id} value={professional.id}>
                        {professional.name}
                      </option>
                    ))}
                  </Input>
                )}
                <FormFeedback>{errors.professional_id}</FormFeedback>
                {restrictProfessionalServices &&
                  serviceEnablement?.restrict_mode &&
                  formServiceId &&
                  !loadingServiceEnablement &&
                  visibleProfessionals.length === 0 && (
                    <Alert color="warning" className="mt-2 mb-0 py-2">
                      <small>{t("studio.agenda.enablement.noProfessionals")}</small>
                    </Alert>
                  )}
              </div>
            </>
          ) : null}

          <div className="mb-3">
            <Label htmlFor="appointment-client">{t("studio.agenda.fields.client.label")}</Label>
            <ClientPicker
              id="appointment-client"
              name="client_id"
              value={formClientId}
              onChange={(clientId) => setFieldValue("client_id", clientId)}
              onBlur={() => setFieldTouched("client_id", true)}
              invalid={!!(touched.client_id && errors.client_id)}
              error={errors.client_id}
              isDisabled={isEditMode}
            />
            {isEditMode ? (
              <small className="text-muted d-block mt-1">
                {t("studio.agenda.fields.client.readOnlyHint")}
              </small>
            ) : null}
          </div>

          {!isEditMode && bookingType === "bundle" ? (
            <div className="mb-3">
              <Label htmlFor="appointment-bundle">{t("studio.agenda.fields.bundle.label")}</Label>
              {loadingBundles ? (
                <div className="py-2">
                  <Spinner size="sm" color="primary" className="me-2" />
                  {t("studio.agenda.bundle.availability.loadingCatalog")}
                </div>
              ) : (
                <Input
                  id="appointment-bundle"
                  name="service_bundle_id"
                  type="select"
                  value={formBundleId}
                  onChange={(event) => handleBundleChange(event.target.value)}
                  onBlur={handleBlur}
                  invalid={!!(touched.service_bundle_id && errors.service_bundle_id)}
                  disabled={!formUnitId}
                >
                  <option value="">{t("studio.agenda.fields.bundle.placeholder")}</option>
                  {serviceBundles.map((bundle) => (
                    <option key={bundle.id} value={bundle.id}>
                      {bundle.name}
                      {bundle.effective_price_cents != null
                        ? ` — ${formatMoney(bundle.effective_price_cents)}`
                        : ""}
                    </option>
                  ))}
                </Input>
              )}
              <FormFeedback>{errors.service_bundle_id}</FormFeedback>
              {bundlesCatalogEmpty ? (
                <Alert color="warning" className="mt-2 mb-0 py-2">
                  <small>{t("studio.agenda.bundle.availability.emptyCatalog")}</small>
                </Alert>
              ) : null}
            </div>
          ) : null}

          <div className="mb-3">
            <Label htmlFor="appointment-date">{t("studio.agenda.fields.date.label")}</Label>
            <Input
              id="appointment-date"
              name="date"
              type="date"
              min={todayIsoDate()}
              value={formDate}
              onChange={(event) => handleAvailabilityFieldChange("date", event.target.value)}
              onBlur={handleBlur}
              invalid={!!(touched.date && errors.date)}
            />
            <FormFeedback>{errors.date}</FormFeedback>
          </div>

          {!isEditMode && onRegisterAbsence && formProfessionalId && formDate ? (
            <div className="mb-3">
              <Button
                type="button"
                color="link"
                className="p-0"
                onClick={() => {
                  const startLocal = createPrefill?.preferred_start
                    ? wallClockToDatetimeLocal(createPrefill.preferred_start)
                    : formSlotKey
                      ? wallClockToDatetimeLocal(formSlotKey.split("|")[0] ?? "")
                      : `${formDate}T12:00`;

                  onRegisterAbsence({
                    professionalId: formProfessionalId,
                    startLocal,
                    endLocal: addMinutesToDatetimeLocal(startLocal, 60),
                  });
                }}
              >
                {t("studio.agenda.absence.fromForm")}
              </Button>
            </div>
          ) : null}

          {(isEditMode || bookingType === "single") ? (
            <div className="mb-3">
              <Label htmlFor="appointment-slot">{t("studio.agenda.fields.slot.label")}</Label>
              {!canFetchSingleAvailability ? (
                <div className="text-muted small py-2">
                  {t("studio.agenda.availability.prerequisites")}
                </div>
              ) : loadingAvailability ? (
                <div className="py-2">
                  <Spinner size="sm" color="primary" className="me-2" />
                  {t("studio.agenda.availability.loading")}
                </div>
              ) : (
                <Input
                  id="appointment-slot"
                  name="slot_key"
                  type="select"
                  value={formSlotKey}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  invalid={!!(touched.slot_key && errors.slot_key)}
                >
                  <option value="">{t("studio.agenda.fields.slot.placeholder")}</option>
                  {slotsForSelect.map((slot) => (
                    <option key={slotKey(slot)} value={slotKey(slot)}>
                      {t("studio.agenda.fields.slot.option", {
                        start: formatTimeLabel(slot.start_datetime),
                        end: formatTimeLabel(slot.end_datetime),
                      })}
                    </option>
                  ))}
                </Input>
              )}
              <FormFeedback>{errors.slot_key}</FormFeedback>
              {availabilityEmptyReason === "allPast" && (
                <Alert color="warning" className="mt-2 mb-0 py-2">
                  <small className="d-block">{t("studio.agenda.availability.emptyAllPast")}</small>
                </Alert>
              )}
              {preferredSingleMissing && (
                <Alert color="warning" className="mt-2 mb-0 py-2">
                  <small className="d-block">
                    {t("studio.agenda.availability.preferredUnavailable", {
                      time: clickedTimeLabel,
                    })}
                  </small>
                </Alert>
              )}
              {singleSlotListHasGap && (
                <Alert color="info" className="mt-2 mb-0 py-2">
                  <small className="d-block">{t("studio.agenda.availability.gapBecauseOfOrganization")}</small>
                </Alert>
              )}
              {availabilityEmptyReason === "noSlots" && (
                <Alert color="warning" className="mt-2 mb-0 py-2">
                  <small className="d-block">{t("studio.agenda.availability.empty")}</small>
                  <small className="d-block mt-1">
                    {t("studio.agenda.availability.emptySetupHint")}
                  </small>
                  <small className="d-block mt-1">
                    <Link to="/units">{t("studio.agenda.availability.configureUnitSchedule")}</Link>
                    {" · "}
                    <Link to="/professionals">
                      {t("studio.agenda.availability.configureRoutine")}
                    </Link>
                  </small>
                </Alert>
              )}
            </div>
          ) : (
            <div className="mb-3">
              <Label>{t("studio.agenda.fields.slot.label")}</Label>
              {!canFetchBundleAvailability ? (
                <div className="text-muted small py-2">
                  {t("studio.agenda.bundle.availability.prerequisites")}
                </div>
              ) : loadingBundleAvailability ? (
                <div className="py-2">
                  <Spinner size="sm" color="primary" className="me-2" />
                  {t("studio.agenda.bundle.availability.loading")}
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {visibleBundleAvailabilitySlots.map((slot) => {
                    const key = slotKey(slot);
                    const isSelected = formBundleSlotKey === key;

                    return (
                      <button
                        key={key}
                        type="button"
                        className={`btn text-start p-3 border rounded ${
                          isSelected ? "border-primary bg-primary-subtle" : "border-light bg-light"
                        }`}
                        onClick={() => {
                          setFieldValue("bundle_slot_key", key);
                          setFieldTouched("bundle_slot_key", true);
                        }}
                        disabled={!formBundleId || !formDate}
                      >
                        <div className="fw-semibold mb-2">
                          {formatTimeRange(slot.start_datetime, slot.end_datetime)}
                        </div>
                        <ul className="list-unstyled mb-0 small">
                          {slot.items.map((item) => (
                            <li key={item.sort_order} className="d-flex flex-wrap gap-1 mb-1">
                              <span className="text-muted">
                                {t("studio.agenda.bundle.slotItem.step", {
                                  step: item.sort_order + 1,
                                })}
                              </span>
                              <span>{resolveBundleServiceName(item.service_id)}</span>
                              <span className="text-muted">·</span>
                              <span>{resolveProfessionalName(item.professional_id)}</span>
                              <span className="text-muted">
                                {t("studio.agenda.bundle.slotItem.time", {
                                  start: formatTimeLabel(item.start_datetime),
                                  end: formatTimeLabel(item.end_datetime),
                                })}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </button>
                    );
                  })}
                </div>
              )}
              {touched.bundle_slot_key && errors.bundle_slot_key ? (
                <div className="invalid-feedback d-block">{errors.bundle_slot_key}</div>
              ) : null}
              {bundleAvailabilityEmptyReason === "allPast" && (
                <Alert color="warning" className="mt-2 mb-0 py-2">
                  <small className="d-block">{t("studio.agenda.availability.emptyAllPast")}</small>
                </Alert>
              )}
              {preferredBundleMissing && (
                <Alert color="warning" className="mt-2 mb-0 py-2">
                  <small className="d-block">
                    {t("studio.agenda.bundle.availability.preferredUnavailable", {
                      time: clickedTimeLabel,
                    })}
                  </small>
                </Alert>
              )}
              {bundleSlotListHasGap && (
                <Alert color="info" className="mt-2 mb-0 py-2">
                  <small className="d-block">{t("studio.agenda.availability.gapBecauseOfOrganization")}</small>
                </Alert>
              )}
              {bundleAvailabilityEmptyReason === "noSlots" && (
                <Alert color="warning" className="mt-2 mb-0 py-2">
                  <small className="d-block">{t("studio.agenda.bundle.availability.empty")}</small>
                  <small className="d-block mt-1">
                    {t("studio.agenda.availability.emptySetupHint")}
                  </small>
                  <small className="d-block mt-1">
                    <Link to="/units">{t("studio.agenda.availability.configureUnitSchedule")}</Link>
                    {" · "}
                    <Link to="/professionals">
                      {t("studio.agenda.availability.configureRoutine")}
                    </Link>
                  </small>
                </Alert>
              )}
            </div>
          )}

          {bookingType === "single" && !isEditMode && selectedService?.require_deposit && (
            <>
              <Alert color="warning" className="mb-3">
                {t("studio.agenda.deposit.warning")}
              </Alert>
              {depositOptions.length > 1 ? (
                <div className="mb-3">
                  <Label htmlFor="appointment-deposit-percent">
                    {t("studio.agenda.fields.depositPercent.label")}
                  </Label>
                  <Input
                    id="appointment-deposit-percent"
                    name="deposit_percent_bps"
                    type="select"
                    value={formDepositPercentBps}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    invalid={!!(touched.deposit_percent_bps && errors.deposit_percent_bps)}
                  >
                    <option value="">{t("studio.agenda.fields.depositPercent.placeholder")}</option>
                    {depositOptions.map((bps) => (
                      <option key={bps} value={bps}>
                        {t("studio.agenda.fields.depositPercent.option", { percent: bps / 100 })}
                      </option>
                    ))}
                  </Input>
                  <FormFeedback>{errors.deposit_percent_bps}</FormFeedback>
                </div>
              ) : null}
              {depositPreviewAmount !== null && (
                <div className="mb-3">
                  <small className="text-muted d-block">
                    {t("studio.agenda.deposit.amountPreview", {
                      amount: formatMoney(depositPreviewAmount),
                    })}
                  </small>
                  {depositOptions.length === 1 ? (
                    <small className="text-muted d-block mt-1">
                      {t("studio.agenda.deposit.singleOptionApplied", {
                        percent: depositOptions[0] / 100,
                      })}
                    </small>
                  ) : null}
                </div>
              )}
              <div className="mb-3">
                <Label htmlFor="appointment-deposit-channel">
                  {t("studio.agenda.deposit.channel.question")}
                </Label>
                {depositMethodsUnavailable ? (
                  <Alert color="warning" className="mb-0">
                    <p className="mb-3">{t("studio.agenda.deposit.empty_methods")}</p>
                    <Button color="primary" size="sm" tag={Link} to="/settings/payments">
                      {t("studio.agenda.deposit.configure_payments")}
                    </Button>
                  </Alert>
                ) : (
                  <>
                    <Input
                      id="appointment-deposit-channel"
                      name="deposit_collection_method"
                      type="select"
                      value={formDepositCollectionMethod}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={
                        !!(touched.deposit_collection_method && errors.deposit_collection_method)
                      }
                    >
                      <option value="">{t("studio.agenda.deposit.channel.placeholder")}</option>
                      {effectiveDepositMethods.map((method) => (
                        <option key={method} value={method}>
                          {t(`studio.agenda.deposit.channel.${method}`)}
                        </option>
                      ))}
                    </Input>
                    <FormFeedback>{errors.deposit_collection_method}</FormFeedback>
                  </>
                )}
              </div>
              {formDepositCollectionMethod === "other" ? (
                <div className="mb-3">
                  <Label htmlFor="appointment-deposit-note">
                    {t("studio.agenda.deposit.channel.noteLabel")}
                  </Label>
                  <Input
                    id="appointment-deposit-note"
                    name="deposit_collection_note"
                    type="text"
                    value={formDepositCollectionNote}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    invalid={
                      !!(touched.deposit_collection_note && errors.deposit_collection_note)
                    }
                  />
                  <FormFeedback>{errors.deposit_collection_note}</FormFeedback>
                </div>
              ) : null}
            </>
          )}

          {(isEditMode || bookingType === "single" || bookingType === "bundle")
            && (clientEmailNotificationsEnabled
              || messagingSettings?.messaging_channel_mode !== "disabled") ? (
            <div className="mb-4 border-top pt-3">
              <p className="fw-medium mb-2">{t("studio.agenda.notifications.title")}</p>
              <p className="text-muted small mb-3">{t("studio.agenda.notifications.help")}</p>
              {emailUsage && whatsAppUsage && messagingSettings ? (
                <p className="text-muted small mb-3">
                  {t("studio.agenda.notifications.creditsHint", {
                    emailCount: emailUsage.transactional_count,
                    emailLimit: emailUsage.limit ?? "—",
                    whatsAppCount: whatsAppUsage.sent_count,
                    whatsAppLimit: whatsAppUsage.effective_limit,
                    whatsAppRemaining: whatsAppUsage.remaining_count,
                  })}
                </p>
              ) : null}
              {emailUsage && isEmailUsageNearLimit(emailUsage) ? (
                <Alert color={emailUsage.is_at_limit ? "danger" : "warning"} className="py-2 px-3">
                  {emailUsage.is_at_limit
                    ? t("studio.agenda.notifications.atLimitHint", {
                        count: emailUsage.transactional_count,
                        limit: emailUsage.limit,
                      })
                    : t("studio.agenda.notifications.nearLimitHint", {
                        count: emailUsage.transactional_count,
                        limit: emailUsage.limit,
                      })}{" "}
                  <Link to="/settings/subscription" className="alert-link">
                    {t("studio.agenda.notifications.upgradeLink")}
                  </Link>
                </Alert>
              ) : null}
              {emailUsage?.is_free && emailUsage.limit !== null && !isEmailUsageNearLimit(emailUsage) ? (
                <p className="text-muted small mb-3">
                  {t("studio.agenda.notifications.usageHint", {
                    count: emailUsage.transactional_count,
                    limit: emailUsage.limit,
                  })}
                </p>
              ) : null}
              {whatsAppUsage
                && messagingSettings
                && isWhatsAppChannelAvailable(messagingSettings, whatsAppUsage)
                && isWhatsAppUsageNearLimit(whatsAppUsage) ? (
                <Alert color="warning" className="py-2 px-3">
                  {t("studio.agenda.notifications.whatsAppNearLimitHint", {
                    count: whatsAppUsage.sent_count,
                    limit: whatsAppUsage.effective_limit,
                  })}{" "}
                  <Link to="/settings/messaging" className="alert-link">
                    {t("studio.agenda.notifications.messagingSettingsLink")}
                  </Link>
                </Alert>
              ) : null}
              {whatsAppUsage
                && messagingSettings
                && messagingSettings.messaging_channel_mode === "pooled"
                && whatsAppUsage.is_at_limit
                && !whatsAppUsage.is_free ? (
                <Alert color="danger" className="py-2 px-3">
                  {t("studio.agenda.notifications.whatsAppAtLimitHint", {
                    count: whatsAppUsage.sent_count,
                    limit: whatsAppUsage.effective_limit,
                  })}{" "}
                  <Link to="/settings/messaging" className="alert-link">
                    {t("studio.agenda.notifications.messagingSettingsLink")}
                  </Link>
                </Alert>
              ) : null}
              {showNoClientPhoneWarning ? (
                <Alert color="warning" className="py-2 px-3">
                  {t("studio.agenda.notifications.noClientPhoneHint")}{" "}
                  <Link to={`/clients/${formClientId}`} className="alert-link">
                    {t("studio.agenda.notifications.noClientPhoneLink")}
                  </Link>
                </Alert>
              ) : null}
              <div className="form-check mb-2">
                <Input
                  id="appointment-notify-created"
                  name="notify_on_created"
                  type="checkbox"
                  className="form-check-input"
                  checked={formNotifyOnCreated}
                  disabled={!emailFeatureSettings.appointment_created_notifications_enabled}
                  onChange={(event) => setFieldValue("notify_on_created", event.target.checked)}
                />
                <Label className="form-check-label" htmlFor="appointment-notify-created">
                  {t("studio.agenda.notifications.notifyOnCreated")}
                </Label>
                {!emailFeatureSettings.appointment_created_notifications_enabled ? (
                  <p className="text-muted small mb-0 mt-1">
                    {t("studio.agenda.notifications.typeADisabledHint")}{" "}
                    <Link to="/settings/email-notifications">
                      {t("studio.agenda.notifications.settingsLink")}
                    </Link>
                  </p>
                ) : null}
              </div>
              <div className="form-check">
                <Input
                  id="appointment-notify-reminder"
                  name="notify_reminder"
                  type="checkbox"
                  className="form-check-input"
                  checked={formNotifyReminder}
                  disabled={!emailFeatureSettings.appointment_reminder_notifications_enabled}
                  onChange={(event) => setFieldValue("notify_reminder", event.target.checked)}
                />
                <Label className="form-check-label" htmlFor="appointment-notify-reminder">
                  {t("studio.agenda.notifications.notifyReminder")}
                </Label>
                {!emailFeatureSettings.appointment_reminder_notifications_enabled ? (
                  <p className="text-muted small mb-0 mt-1">
                    {t("studio.agenda.notifications.typeBDisabledHint")}{" "}
                    <Link to="/settings/email-notifications">
                      {t("studio.agenda.notifications.settingsLink")}
                    </Link>
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="d-flex gap-2 justify-content-end">
            <Button color="light" onClick={handleClose}>
              {t("studio.agenda.actions.cancel")}
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={submitting || depositMethodsUnavailable}
            >
              {submitting
                ? t("studio.agenda.loading")
                : isEditMode
                  ? t("studio.agenda.actions.edit.save")
                  : t("studio.agenda.actions.save")}
            </Button>
          </div>
        </Form>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default AppointmentFormOffcanvas;
